'use client';

import React, { createContext, useContext, useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '@/redux/Hook';
import { RootState } from '@/redux';
import { fetchWholesalerBrandingByDomain } from '@/redux/wholesalerBranding/wholesalerBrandingSlice';

interface WholesalerBrandingCtx {
  branding: RootState['wholesalerBranding']['branding'];
  isWhitelabel: boolean;
  brandName: string;
}

const Ctx = createContext<WholesalerBrandingCtx>({
  branding: null,
  isWhitelabel: false,
  brandName: 'cresivox',
});

const KNOWN_1CB_DOMAINS = ['cresivox.com', 'localhost'];

function isOwnDomain(hostname: string) {
  return KNOWN_1CB_DOMAINS.some((d) => hostname === d || hostname.endsWith(`.${d}`));
}

export function WholesalerBrandingProvider({ children }: { children: React.ReactNode }) {
  const dispatch = useAppDispatch();
  const { branding, isWhiteLabelDomain } = useAppSelector((s: RootState) => s.wholesalerBranding);

  useEffect(() => {
    const hostname = window.location.hostname;
    if (!isOwnDomain(hostname)) {
      dispatch(fetchWholesalerBrandingByDomain(hostname));
    }
  }, [dispatch]);

  // A supplier's own panel domain shows the supplier's icon in the tab (its favicon, else its logo), not Cresivox's.
  const tabIcon = branding?.favicon_url || branding?.logo_url || null;
  useEffect(() => {
    if (!tabIcon || !/^https:\/\//i.test(tabIcon)) return;
    document.querySelectorAll('link[rel~="icon"]').forEach((el) => el.remove());
    const link = document.createElement('link');
    link.rel = 'icon';
    link.href = tabIcon;
    document.head.appendChild(link);
  }, [tabIcon]);

  return (
    <Ctx.Provider
      value={{
        branding,
        isWhitelabel: isWhiteLabelDomain,
        brandName: branding?.brand_name || 'cresivox',
      }}
    >
      {children}
    </Ctx.Provider>
  );
}

export function useWholesalerBranding() {
  return useContext(Ctx);
}
